import { getConsentSnapshot, hasAnalyticsConsent } from "../utils/cookieConsent";
import { analyticsConfig } from "./config";
import { BUSINESS_EVENT_CONTRACT, BUSINESS_EVENT_NAMES, BUSINESS_EVENT_SCHEMA } from "./eventContract";

const ANALYTICS_BUFFER_KEY = "xphub_analytics_events_v1";
const GA_SCRIPT_ID = "xphub-ga4-script";

const bufferedEvents = [];
let gaReadyPromise = null;
let lastPageView = { path: "", at: 0 };

function readSessionId() {
  if (typeof window === "undefined") return "server";
  try {
    const existing = sessionStorage.getItem("xphub_analytics_session_id");
    if (existing) return existing;
    const created = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("xphub_analytics_session_id", created);
    return created;
  } catch {
    return `fallback-${Date.now()}`;
  }
}

function currentPath() {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname || "/"}${window.location.search || ""}`;
}

function sanitizePayload(payload) {
  if (!payload || typeof payload !== "object") return {};

  const out = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined) continue;
    if (value === null) {
      out[key] = null;
      continue;
    }
    if (typeof value === "number") {
      out[key] = Number.isFinite(value) ? value : null;
      continue;
    }
    if (typeof value === "boolean") {
      out[key] = value;
      continue;
    }
    if (typeof value === "string") {
      out[key] = value.trim().slice(0, 240);
      continue;
    }
    out[key] = String(value).slice(0, 240);
  }
  return out;
}

function persistBufferedEvents() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(ANALYTICS_BUFFER_KEY, JSON.stringify(bufferedEvents));
  } catch {
    // noop
  }
}

function exposeDebugApi() {
  if (typeof window === "undefined") return;
  window.__XPHUB_ANALYTICS__ = {
    getEvents: () => [...bufferedEvents],
    contract: BUSINESS_EVENT_CONTRACT,
    config: {
      appEnv: analyticsConfig.appEnv,
      provider: analyticsConfig.provider,
      enabled: analyticsConfig.enabled,
    },
  };
}

function bufferEvent(event) {
  bufferedEvents.push(event);
  if (bufferedEvents.length > analyticsConfig.maxBufferedEvents) {
    bufferedEvents.splice(0, bufferedEvents.length - analyticsConfig.maxBufferedEvents);
  }
  persistBufferedEvents();

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("xphub:analytics-event", { detail: event }));
  }
}

function ensureGtagBase() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

function gaDisableFlagName() {
  return `ga-disable-${analyticsConfig.gaMeasurementId}`;
}

function setGaDisabled(disabled) {
  if (typeof window === "undefined") return;
  window[gaDisableFlagName()] = Boolean(disabled);
}

function mapConsentForGa(consent) {
  return {
    analytics_storage: consent?.analytics ? "granted" : "denied",
    ad_storage: consent?.ads ? "granted" : "denied",
    ad_user_data: consent?.ads ? "granted" : "denied",
    ad_personalization: consent?.ads ? "granted" : "denied",
  };
}

export function syncConsentMode(consent = getConsentSnapshot()) {
  const analyticsAllowed = analyticsConfig.enabled && Boolean(consent?.analytics);
  setGaDisabled(!analyticsAllowed);

  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", mapConsentForGa(consent));
}

export function ensureAnalyticsProviderReady() {
  if (!analyticsConfig.enabled || analyticsConfig.provider !== "ga4") {
    return Promise.resolve(false);
  }

  if (typeof window === "undefined") return Promise.resolve(false);

  if (typeof window.gtag === "function") {
    ensureGtagBase();
    return Promise.resolve(true);
  }

  if (gaReadyPromise) return gaReadyPromise;

  gaReadyPromise = new Promise((resolve) => {
    ensureGtagBase();

    // Consent Mode v2: set denied defaults BEFORE the GA script loads so no
    // data is collected prior to explicit user consent (GDPR requirement).
    const initialConsent = getConsentSnapshot();
    window.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });
    // Apply any existing stored consent immediately.
    window.gtag("consent", "update", mapConsentForGa(initialConsent));

    const existing = document.getElementById(GA_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(analyticsConfig.gaMeasurementId)}`;
    script.onload = () => {
      ensureGtagBase();
      window.gtag("js", new Date());
      window.gtag("config", analyticsConfig.gaMeasurementId, { send_page_view: false });
      resolve(true);
    };
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return gaReadyPromise;
}

function buildEnvelope(name, payload, meta = {}) {
  const consent = getConsentSnapshot();
  return {
    category: BUSINESS_EVENT_CONTRACT.category,
    event_name: name,
    event_version: BUSINESS_EVENT_CONTRACT.schemaVersion,
    occurred_at: new Date().toISOString(),
    app_env: analyticsConfig.appEnv,
    path: currentPath(),
    session_id: readSessionId(),
    source: String(meta.source || "frontend"),
    user_id: meta.userId ? String(meta.userId) : null,
    consent: {
      analytics: Boolean(consent.analytics),
      preferences: Boolean(consent.preferences),
      ads: Boolean(consent.ads),
    },
    payload: sanitizePayload(payload),
  };
}

function validateEventPayload(eventName, payload = {}) {
  const schema = BUSINESS_EVENT_SCHEMA[eventName];
  if (!schema) return false;

  for (const requiredKey of schema.required) {
    if (!(requiredKey in payload)) return false;
  }
  return true;
}

function toGaParams(envelope) {
  const params = {
    event_version: envelope.event_version,
    app_env: envelope.app_env,
    path: envelope.path,
    session_id: envelope.session_id,
    source: envelope.source,
    ...envelope.payload,
  };

  if (envelope.user_id) {
    params.user_id = envelope.user_id;
  }

  return params;
}

export async function trackBusinessEvent(eventName, payload = {}, meta = {}) {
  if (!BUSINESS_EVENT_NAMES.includes(eventName)) {
    if (analyticsConfig.debug) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] evento no soportado:", eventName);
    }
    return false;
  }

  const cleanPayload = sanitizePayload(payload);
  if (!validateEventPayload(eventName, cleanPayload)) {
    if (analyticsConfig.debug) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] payload inválido para", eventName, cleanPayload);
    }
    return false;
  }

  if (!analyticsConfig.enabled || !hasAnalyticsConsent()) {
    return false;
  }

  const envelope = buildEnvelope(eventName, cleanPayload, meta);

  const ready = await ensureAnalyticsProviderReady();
  if (!ready || typeof window === "undefined" || typeof window.gtag !== "function") {
    return false;
  }

  syncConsentMode();
  window.gtag("event", eventName, toGaParams(envelope));
  bufferEvent(envelope);

  if (analyticsConfig.debug) {
    // eslint-disable-next-line no-console
    console.info("[analytics] event", envelope);
  }

  return true;
}

export async function trackPageView(path = currentPath()) {
  if (!analyticsConfig.enabled || !hasAnalyticsConsent()) return false;

  const now = Date.now();
  if (lastPageView.path === path && now - lastPageView.at < 400) {
    return false;
  }
  lastPageView = { path, at: now };

  const ready = await ensureAnalyticsProviderReady();
  if (!ready || typeof window === "undefined" || typeof window.gtag !== "function") {
    return false;
  }

  syncConsentMode();
  window.gtag("config", analyticsConfig.gaMeasurementId, {
    page_path: path,
  });

  return true;
}

exposeDebugApi();
